import { makeAutoObservable, toJS } from 'mobx'

import { CountAndClass } from 'widgets/level-class-count'

import { getOrCreateAviaSearchId } from 'entities/ticket/lib/avia-search-id'
import { isDifficultAviaRoute } from 'entities/ticket/lib/is-difficult-avia-route'
import { normalizeRegularSearchDirections } from 'entities/ticket/lib/normalize-regular-search-directions'

import { TicketClass } from 'shared/constants'
import { DATE_FORMAT_LIST } from 'shared/constants/dateFormatList'
import { dateFormat, getIsBeforeDateAndSame } from 'shared/lib/dateFormat'

import listTicketsStore from '../list-tickets/list-tickets-store'

import {
  DataForFilter,
  DifficultRoute,
  FilterTicketForm,
  IFindTicketForm,
  RangeDate,
  SetDirection,
} from './models'

const initialState: IFindTicketForm = {
  directions: [{}],
  cabin_class: TicketClass.e.key,
  adult: 1,
  child: 0,
  infant: 0,
  infants_with_place: 0,
}

class FindTicketFormStore {
  formFindTicket: IFindTicketForm = initialState
  copyFormFindTicket: IFindTicketForm | null = null
  dataForFilter: DataForFilter | null = null

  constructor() {
    makeAutoObservable(this)
  }

  setDataForFilter(filterData: DataForFilter) {
    this.dataForFilter = filterData
  }

  resetDataForFilter() {
    this.dataForFilter = null
  }

  resetFilter() {
    this.resetDataForFilter()

    if (this.copyFormFindTicket) {
      this.copyFormFindTicket = {
        ...this.copyFormFindTicket,
        filter: undefined,
      }
    }

    if (this.formFindTicket) {
      this.formFindTicket = {
        ...this.formFindTicket,
        filter: undefined,
      }
    }
  }

  onSelectDirectionFrom({ depp, index = 0, isCopy }: SetDirection) {
    if (isCopy && this.copyFormFindTicket) {
      this.copyFormFindTicket.directions[index].from = depp
    } else {
      this.formFindTicket.directions[index].from = depp
    }

    this.resetFilter()
  }

  onSelectDirectionTo({ depp, index = 0, isCopy }: SetDirection) {
    if (isCopy && this.copyFormFindTicket) {
      this.copyFormFindTicket.directions[index].to = depp
    } else {
      this.formFindTicket.directions[index].to = depp
    }
    this.resetFilter()
  }

  onSetCountAndClass(data?: CountAndClass, isCopy?: boolean) {
    if (isCopy && this.copyFormFindTicket !== null) {
      this.copyFormFindTicket = {
        ...this.copyFormFindTicket,
        ...data,
        filter: undefined,
      }

      return
    }

    this.formFindTicket = {
      ...this.formFindTicket,
      ...data,
      filter: undefined,
    }
  }

  onSwitchDirection(index: number) {
    const directions = toJS(this.formFindTicket.directions[index])
    const from = { ...directions.from }

    directions.from = directions.to

    directions.to = {
      ...from,
      cityName: from.cityName || '',
      countryName: from.countryName || '',
      cityIataCode: from.cityIataCode || '',
    }

    this.formFindTicket.directions[index] = directions

    this.resetFilter()
  }

  onSetRangeDate({ rangeData, indexDirect = 0, isCopy }: RangeDate) {
    this.resetFilter()

    const updateDirection = (directions: DifficultRoute[]) => {
      const updatedDirection = {
        ...directions[indexDirect],
        date: rangeData?.startDate,
        back_date: rangeData?.endDate,
      }

      return normalizeRegularSearchDirections([
        updatedDirection,
        ...directions.slice(1),
      ])
    }

    if (isCopy && this.copyFormFindTicket !== null) {
      this.copyFormFindTicket = {
        ...this.copyFormFindTicket,
        directions: updateDirection(this.copyFormFindTicket.directions),
      }

      return
    }

    this.formFindTicket = {
      ...this.formFindTicket,
      directions: updateDirection(this.formFindTicket.directions),
    }
  }

  onSelectRecommendation(data: DifficultRoute) {
    this.formFindTicket = {
      ...this.formFindTicket,
      directions: [data],
      filter: undefined,
    }
  }

  onAddDirection() {
    const lastArr = this.formFindTicket.directions?.at(-1)

    this.formFindTicket = {
      ...this.formFindTicket,
      directions: [...this.formFindTicket.directions, { from: lastArr?.to }],
      filter: undefined,
    }
  }

  deleteDifficultRoute(index: number) {
    const listRoutes = [...this.formFindTicket.directions]

    listRoutes?.splice(index, 1)

    this.formFindTicket.directions = listRoutes
  }

  onChangeDirectionDates(data: DifficultRoute[]) {
    this.formFindTicket = {
      ...this.formFindTicket,
      directions: [...data],
    }

    this.resetFilter()
  }

  onPrepareSearch() {
    this.formFindTicket.filter = undefined

    listTicketsStore.resetListOfTicket()
  }

  onPrepareRegularSearch(isCopy?: boolean) {
    if (isCopy && this.copyFormFindTicket !== null) {
      this.copyFormFindTicket = {
        ...this.copyFormFindTicket,
        directions: normalizeRegularSearchDirections(
          this.copyFormFindTicket.directions,
        ),
        filter: undefined,
      }

      getOrCreateAviaSearchId(this.copyFormFindTicket)

      return
    }

    this.formFindTicket = {
      ...this.formFindTicket,
      directions: normalizeRegularSearchDirections(
        this.formFindTicket.directions,
      ),
      filter: undefined,
    }

    getOrCreateAviaSearchId(this.formFindTicket)
    listTicketsStore.resetListOfTicket()
  }

  onGoToDifficultSearch() {
    this.onPrepareSearch()

    if (isDifficultAviaRoute(this.formFindTicket.directions)) {
      this.formFindTicket.directions = this.formFindTicket.directions.map(
        (direction) => ({ ...direction, back_date: '' }),
      )
    }

    getOrCreateAviaSearchId(this.formFindTicket)
  }

  onSetFilter(filterData?: FilterTicketForm) {
    this.formFindTicket.filter = filterData

    listTicketsStore.resetListOfTicket()
  }

  getCountPassengers() {
    return {
      adult: this.formFindTicket.adult,
      child: this.formFindTicket.child,
      infant: this.formFindTicket.infant,
      infants_with_place: this.formFindTicket.infants_with_place,
    }
  }

  onBuyForMySelf(variant: boolean) {
    this.formFindTicket.forMySelf = variant
  }

  resetDirection() {
    this.formFindTicket = initialState
  }

  getCountOfPassengers(): number {
    const passengers = this.formFindTicket

    return (
      (passengers?.adult || 0) +
      (passengers?.child || 0) +
      (passengers?.infant || 0) +
      (passengers?.infants_with_place || 0)
    )
  }

  setCopyForSearch() {
    if (this.copyFormFindTicket) {
      this.formFindTicket = toJS(this.copyFormFindTicket)
      listTicketsStore.resetListOfTicket()
    }
  }

  onSetDataCopy() {
    this.copyFormFindTicket = toJS(this.formFindTicket)
  }

  resetDirectionCopy() {
    this.copyFormFindTicket = null
  }

  onSwitchDirectionCopy(index: number) {
    if (!this.copyFormFindTicket?.directions) return

    const directions = toJS(this.copyFormFindTicket.directions[index])
    const from = { ...directions.from }

    directions.from = directions.to

    directions.to = {
      ...from,
      cityName: from.cityName || '',
      countryName: from.countryName || '',
      cityIataCode: from.cityIataCode || '',
    }

    this.copyFormFindTicket.directions[index] = directions

    this.resetFilter()
  }

  getFormFindTicketDirections() {
    return toJS(this.formFindTicket?.directions)
  }

  setFirstForm(direction?: DifficultRoute) {
    if (direction) {
      this.formFindTicket = { ...this.formFindTicket, directions: [direction] }
    }
  }

  setFormFindTicket(data: IFindTicketForm) {
    this.formFindTicket = {
      ...this.formFindTicket,
      ...data,
    }
  }

  setFormFromLocalStorage(data: IFindTicketForm) {
    const currentDate = dateFormat(
      new Date().toISOString(),
      DATE_FORMAT_LIST.YEAR_FULL_DATE,
    )

    const isValidDate = getIsBeforeDateAndSame(
      currentDate,
      data?.directions?.[0]?.date,
    )

    if (!isValidDate) data.directions[0].date = currentDate

    this.setFormFindTicket(data)
  }
}

export default new FindTicketFormStore()
